import { useEffect, useLayoutEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import { Box } from '@mui/material';
import { Terminal as XTerm } from 'xterm';
import { FitAddon } from '@xterm/addon-fit';
import 'xterm/css/xterm.css';

export interface TerminalHandle {
  write: (data: string | Uint8Array) => void;
}

interface TerminalProps {
  onData?: (data: string | Uint8Array) => void;
  onResize?: (cols: number, rows: number) => void;
}

export const Terminal = forwardRef<TerminalHandle, TerminalProps>(function Terminal(
  { onData, onResize },
  ref
) {
  const containerRef = useRef<HTMLDivElement>(null);
  const xtermRef = useRef<XTerm | null>(null);
  const fitAddonRef = useRef<FitAddon | null>(null);
  const utf8DecoderRef = useRef(new TextDecoder());

  // Stable refs for callbacks — xterm effect runs once, but always calls latest callbacks
  const onDataRef = useRef(onData);
  const onResizeRef = useRef(onResize);
  useLayoutEffect(() => {
    onDataRef.current = onData;
    onResizeRef.current = onResize;
  });

  useImperativeHandle(ref, () => ({
    write: (data: string | Uint8Array) => {
      if (!xtermRef.current) return;
      if (typeof data === 'string') {
        xtermRef.current.write(data);
        return;
      }
      // Decode chunks to UTF-8 text before writing to xterm to keep output
      // compatible across xterm API variants.
      xtermRef.current.write(utf8DecoderRef.current.decode(data, { stream: true }));
    },
  }));

  // xterm initialised once; stable because deps are empty — callbacks accessed via refs
  useEffect(() => {
    if (!containerRef.current) return;

    // Fixed dark theme (VS Code Dark+) — avoids white background in light MUI themes
    const xtermTheme = {
      background: '#1e1e1e',
      foreground: '#d4d4d4',
      cursor: '#ffffff',
      cursorAccent: '#1e1e1e',
      selectionBackground: '#264f78',
      black: '#000000',
      white: '#d4d4d4',
      brightBlack: '#808080',
      brightWhite: '#ffffff',
      red: '#cd3131',
      green: '#0dbc79',
      yellow: '#e5e510',
      blue: '#2472c8',
      magenta: '#bc3fbc',
      cyan: '#11a8cd',
      brightRed: '#f14c4c',
      brightGreen: '#23d18b',
      brightYellow: '#f5f543',
      brightBlue: '#3b8eea',
      brightMagenta: '#d670d6',
      brightCyan: '#29b8db',
    };

    const xterm = new XTerm({
      cursorBlink: true,
      fontSize: 14,
      fontFamily: 'Menlo, Monaco, "Courier New", monospace',
      theme: xtermTheme,
      scrollback: 5000,
      allowProposedApi: true,
    });

    const fitAddon = new FitAddon();
    xterm.loadAddon(fitAddon);

    xterm.open(containerRef.current);
    if (typeof (xterm as unknown as { focus?: () => void }).focus === 'function') {
      xterm.focus();
    }
    fitAddon.fit();

    xtermRef.current = xterm;
    fitAddonRef.current = fitAddon;

    onResizeRef.current?.(xterm.cols, xterm.rows);

    xterm.onData((data) => {
      onDataRef.current?.(data);
    });

    const doFit = () => {
      if (fitAddonRef.current && xtermRef.current) {
        fitAddonRef.current.fit();
        onResizeRef.current?.(xtermRef.current.cols, xtermRef.current.rows);
      }
    };

    const resizeObserver = new ResizeObserver(doFit);
    resizeObserver.observe(containerRef.current);

    window.addEventListener('resize', doFit);

    // Second fit after layout settles — catches container height changes from async CSS
    const timeoutId = setTimeout(doFit, 150);

    return () => {
      window.removeEventListener('resize', doFit);
      resizeObserver.disconnect();
      clearTimeout(timeoutId);
      xterm.dispose();
      xtermRef.current = null;
      fitAddonRef.current = null;
    };
  }, []); // empty: xterm created once for the lifetime of this component

  return (
    <Box
      ref={containerRef}
      onClick={() => xtermRef.current?.focus()}
      sx={{
        width: '100%',
        height: '100%',
        cursor: 'text',
        // xterm element must fill the container so FitAddon calculates dimensions correctly
        '& .xterm': {
          height: '100%',
        },
        // Override xterm-viewport background to match the fixed dark theme
        '& .xterm-viewport': {
          backgroundColor: '#1e1e1e',
        },
      }}
    />
  );
});
